import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as uploadsActions from '@payaca/store/uploads/uploadsActions';

import useGetScheduledEvent from '@/api/queries/scheduleEvents/useGetScheduledEvent';
import useLinkFolderEntriesToEvent from './useLinkFolderEntriesToEvent';

interface UseLinkFilesToEventParams {
  eventId: string | number;
}

interface LinkFilesToEventParams {
  uploadIds: number[];
  folderEntryIds: string[];
  onSuccess?: () => void;
  onError?: () => void;
}

const useLinkFilesToEvent = ({ eventId }: UseLinkFilesToEventParams) => {
  const dispatch = useDispatch();
  const [isLinking, setIsLinking] = useState(false);

  const { data } = useGetScheduledEvent(eventId);

  const linkFolderEntriesMutation = useLinkFolderEntriesToEvent({
    eventId: data?.scheduledEvent?.publicId || '',
  });

  const linkFilesToEvent = useCallback(
    async ({
      uploadIds,
      folderEntryIds,
      onSuccess,
      onError,
    }: LinkFilesToEventParams) => {
      if (!data?.scheduledEvent) return;

      setIsLinking(true);

      const hasUploads = uploadIds.length > 0;
      const hasFolderEntries = folderEntryIds.length > 0;
      const totalOperations = (hasUploads ? 1 : 0) + (hasFolderEntries ? 1 : 0);
      let completedOperations = 0;
      let hasError = false;

      const checkCompletion = () => {
        completedOperations++;
        if (completedOperations === totalOperations) {
          setIsLinking(false);
          if (hasError) {
            onError?.();
          } else {
            onSuccess?.();
          }
        }
      };

      const handleError = () => {
        hasError = true;
        checkCompletion();
      };

      // Link uploads via Redux action (single operation for all uploads)
      if (hasUploads) {
        dispatch(
          uploadsActions.linkUploadsToEntity.request({
            payload: {
              uploadIds,
              entityType: 'scheduledEvent',
              entityId: Number(data.scheduledEvent.id),
            },
            callback: checkCompletion,
            onErrorCallback: handleError,
          })
        );
      }

      // Link folder entries via TanStack Query mutation (single batch operation)
      if (hasFolderEntries) {
        try {
          await linkFolderEntriesMutation.mutateAsync({
            eventId: data.scheduledEvent.publicId,
            folderEntryIds,
          });
          checkCompletion();
        } catch (_error) {
          handleError();
        }
      }

      // Handle edge case where there are no operations
      if (totalOperations === 0) {
        setIsLinking(false);
        onSuccess?.();
      }
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: eventId
    [data, dispatch, eventId, linkFolderEntriesMutation]
  );

  return {
    linkFilesToEvent,
    isLinking,
  };
};

export default useLinkFilesToEvent;
